import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { cn } from '@/utils/cn';

export interface MarkdownRendererProps {
  /**
   * The markdown content to render
   */
  content: string;
  
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string;
  
  /**
   * Color theme for the prose styling
   * @default 'amber'
   */
  proseColor?: 'amber' | 'orange' | 'red' | 'blue' | 'slate';
  
  /**
   * Whether to allow dangerous HTML (not recommended for user content)
   * @default false
   */
  allowDangerousHtml?: boolean;
}

/**
 * MarkdownRenderer - Secure markdown renderer with heritage design system
 * 
 * Features:
 * - GitHub Flavored Markdown support (tables, strikethrough, etc.)
 * - XSS protection via sanitization
 * - Heritage theme integration
 * - Responsive typography
 * - Code syntax highlighting ready
 * 
 * Security:
 * - All HTML is sanitized by default
 * - External links get security attributes
 * - Only safe markdown elements allowed
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  proseColor = 'amber',
  allowDangerousHtml = false
}) => {
  // If content is empty or just whitespace, render as plain text
  if (!content || !content.trim()) {
    return (
      <div className={cn('text-gray-500 italic', className)}>
        No content available
      </div>
    );
  }

  // Custom components for better styling and security
  const components = {
    // External links should be secure
    a: ({ href, children, ...props }: any) => {
      const isExternal = href && (href.startsWith('http') || href.startsWith('//'));
      return (
        <a
          href={href}
          {...props}
          {...(isExternal && {
            target: '_blank',
            rel: 'noopener noreferrer'
          })}
          className="text-blue-600 hover:text-blue-800 underline transition-colors"
        >
          {children}
        </a>
      );
    },
    
    // Code blocks with heritage styling
    code: ({ inline, children, ...props }: any) => {
      if (inline) {
        return (
          <code
            {...props}
            className="px-1.5 py-0.5 text-sm bg-gray-100 text-gray-800 rounded border font-mono"
          >
            {children}
          </code>
        );
      }
      return (
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto">
          <code {...props} className="text-sm font-mono text-gray-800">
            {children}
          </code>
        </pre>
      );
    },

    // Tables with heritage styling
    table: ({ children, ...props }: any) => (
      <div className="overflow-x-auto my-4">
        <table {...props} className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
          {children}
        </table>
      </div>
    ),
    
    th: ({ children, ...props }: any) => (
      <th {...props} className="px-4 py-2 bg-gray-50 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
        {children}
      </th>
    ),
    
    td: ({ children, ...props }: any) => (
      <td {...props} className="px-4 py-2 text-sm text-gray-800 border-b border-gray-100">
        {children}
      </td>
    ),

    // Blockquotes with heritage styling
    blockquote: ({ children, ...props }: any) => (
      <blockquote 
        {...props} 
        className={cn(
          'border-l-4 pl-4 py-2 my-4 italic',
          proseColor === 'amber' && 'border-amber-400 bg-amber-50/50 text-amber-900',
          proseColor === 'orange' && 'border-orange-400 bg-orange-50/50 text-orange-900',
          proseColor === 'red' && 'border-red-400 bg-red-50/50 text-red-900',
          proseColor === 'blue' && 'border-blue-400 bg-blue-50/50 text-blue-900',
          proseColor === 'slate' && 'border-slate-400 bg-slate-50/50 text-slate-900'
        )}
      >
        {children}
      </blockquote>
    )
  };

  // Determine prose classes based on color theme
  const proseClasses = cn(
    'prose max-w-none',
    proseColor === 'amber' && 'prose-amber',
    proseColor === 'orange' && 'prose-orange', 
    proseColor === 'red' && 'prose-red',
    proseColor === 'blue' && 'prose-blue',
    proseColor === 'slate' && 'prose-slate',
    // Heritage color overrides
    'prose-headings:text-amber-900 prose-strong:text-amber-900',
    'prose-a:text-blue-600 hover:prose-a:text-blue-800',
    'prose-code:text-gray-800 prose-pre:bg-gray-50'
  );

  return (
    <div className={cn(proseClasses, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={allowDangerousHtml ? [] : [rehypeSanitize]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;